import { clamp } from '../core/math';
import type { DayNumber } from '../core/time';
import { B, inWinter } from './constants';
import { planProgress } from './forecast';
import type { Simulation, Site, SiteState } from './types';
import { workAt } from './workCurve';

/** 工種 i の、日 T での進み具合（0〜1）。土工は累計稼働日の比率、ほかは日数に比例。 */
export function taskProgress(
  s: Pick<Site, 'tasks'>,
  i: number,
  T: DayNumber,
  sim: Simulation,
): number {
  const t = s.tasks[i]!;
  const f = sim.fc[i]!;
  if (t.earth) {
    const W = (d: number) => workAt(sim.cw, d);
    const r = (a: number, b: number) => clamp((W(T) - W(a)) / Math.max(1e-6, W(b) - W(a)), 0, 1);
    if (t.a >= 1) return r(t.ts, f.F);
    if (t.a > 0) return T <= B ? t.a * r(t.ts, B) : t.a + (1 - t.a) * r(B, f.F);
    return r(f.S, f.F);
  }
  if (t.a >= 1) return clamp((T - t.ts) / Math.max(1, f.F - t.ts), 0, 1);
  if (t.a > 0) {
    if (T <= B) return t.a * clamp((T - t.ts) / Math.max(1, B - t.ts), 0, 1);
    return t.a + (1 - t.a) * clamp((T - B) / Math.max(1, f.F - B), 0, 1);
  }
  return clamp((T - f.S) / Math.max(1, f.F - f.S), 0, 1);
}

/** 日 T の現場の状態 S(t)。 */
export function stateAt(
  s: Pick<Site, 'tasks' | 'region'>,
  T: DayNumber,
  sim: Simulation,
): SiteState {
  const roles: SiteState['roles'] = {};
  let overall = 0;
  let plan = 0;
  let e = 0;
  s.tasks.forEach((t, i) => {
    const p = taskProgress(s, i, T, sim);
    overall += t.w * p;
    plan += t.w * planProgress(t, T);
    if (t.role) roles[t.role] = p;
    if (t.earth) e = p;
  });
  return { T, overall, plan, e, roles, winter: inWinter(s.region, T) };
}

/** 基準日の実績進捗（重みつき）。 */
export const actualAtBase = (s: Pick<Site, 'tasks'>): number =>
  s.tasks.reduce((a, t) => a + t.w * t.a, 0);

/** 基準日の計画進捗（重みつき）。 */
export const planAtBase = (s: Pick<Site, 'tasks'>): number =>
  s.tasks.reduce((a, t) => a + t.w * planProgress(t, B), 0);
